import { useCallback, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import type { VolumeCapacityConfig } from '@/features/bidding/types/bidLadderInput'
import type {
  BidLevelActivationGroupSymmetrical,
  BidLevelInputSymmetrical,
  DragPayloadSymmetrical,
} from '@/features/bidding/types/bidLadderInputSymmetrical'
import {
  createDefaultVolumeCapacityConfig,
  DEFAULT_PRICES_MFRR,
  MAX_BID_LEVELS_MFRR,
} from '@/features/bidding/utils/bidLadderInput/constants'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

function createInitialLevels(
  maxBidLevels: number,
  defaultPrices: number[],
  withVolumeCapacity = false,
): BidLevelInputSymmetrical[] {
  return Array.from({ length: maxBidLevels }, (_, index) => ({
    id: uuidv4(),
    price: defaultPrices[index],
    assignedGroups: [],
    ...(withVolumeCapacity && {
      volumeCapacity: createDefaultVolumeCapacityConfig(),
    }),
  }))
}

export type UseBidLadderInputStateSymmetricalReturn = {
  levels: BidLevelInputSymmetrical[]
  updatePrice: (levelId: string, price: number | null) => void
  assignGroups: (levelId: string, payload: DragPayloadSymmetrical) => void
  /** Removes all activation groups from the same regulation object */
  unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
  getTotalMW: (levelId: string) => number
  /** Returns total MW across ALL levels (for relative bar width) */
  getTotalMWAcrossAllLevels: () => number
  /** Memoized set of all assigned group UUIDs */
  assignedGroupUuids: Set<string>
  /** Resets all levels to initial state, removing all assigned groups */
  resetLevels: () => void
  /** Updates volume capacity configuration for a level */
  updateVolumeCapacity: (levelId: string, config: VolumeCapacityConfig) => void
}

type UseBidLadderInputStateSymmetricalOptions = {
  /** Default prices per level */
  defaultPrices?: number[]
  /** Maximum number of bid levels (defaults to MFRR = 3) */
  maxBidLevels?: number
  /** Whether to initialize volume capacity configuration (for FCR markets without activation groups) */
  withVolumeCapacity?: boolean
}

export function useBidLadderInputStateSymmetrical(
  options: UseBidLadderInputStateSymmetricalOptions = {},
): UseBidLadderInputStateSymmetricalReturn {
  const {
    defaultPrices = DEFAULT_PRICES_MFRR,
    maxBidLevels = MAX_BID_LEVELS_MFRR,
    withVolumeCapacity = false,
  } = options
  const [levels, setLevels] = useState<BidLevelInputSymmetrical[]>(() =>
    createInitialLevels(maxBidLevels, defaultPrices, withVolumeCapacity),
  )

  const updatePrice = useCallback((levelId: string, price: number | null) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          price,
        }
      }),
    )
  }, [])

  const assignGroups = useCallback((levelId: string, payload: DragPayloadSymmetrical) => {
    setLevels((prev) => {
      // If moving from another level, handle as a move operation
      if (payload.sourceLevelId) {
        // Don't allow dropping on the same level
        if (payload.sourceLevelId === levelId) return prev

        // Find the source level and get the groups to move (by regulation object code)
        const sourceLevel = prev.find((l) => l.id === payload.sourceLevelId)
        if (!sourceLevel) return prev

        const regulationObjectCodes = new Set(payload.regulationObjectCodes)
        const groupsToMove = sourceLevel.assignedGroups.filter((g) => regulationObjectCodes.has(g.regulationObjectCode))

        if (groupsToMove.length === 0) return prev

        return prev.map((level) => {
          if (level.id === payload.sourceLevelId) {
            // Remove groups from source level
            return {
              ...level,
              assignedGroups: level.assignedGroups.filter((g) => !regulationObjectCodes.has(g.regulationObjectCode)),
            }
          }
          if (level.id === levelId) {
            // Add groups to target level
            return {
              ...level,
              assignedGroups: [...level.assignedGroups, ...groupsToMove],
            }
          }
          return level
        })
      }

      // New groups from sidebar - convert and add
      const alreadyAssigned = new Set<string>()
      prev.forEach((level) => {
        level.assignedGroups.forEach((group) => {
          alreadyAssigned.add(group.uuid)
        })
      })

      const newGroups: BidLevelActivationGroupSymmetrical[] = payload.activationGroups
        .filter((ag) => !alreadyAssigned.has(ag.uuid))
        .map((ag) => ({
          uuid: ag.uuid,
          code: ag.code,
          regulationObjectCode: ag.regulationObjectCode,
          capacityMW: wattHoursToMegawattHours(ag.capacityMW),
        }))

      if (newGroups.length === 0) return prev

      return prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          assignedGroups: [...level.assignedGroups, ...newGroups],
        }
      })
    })
  }, [])

  const unassignRegulationObjectGroup = useCallback((levelId: string, regulationObjectCode: string) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          assignedGroups: level.assignedGroups.filter((g) => g.regulationObjectCode !== regulationObjectCode),
        }
      }),
    )
  }, [])

  const getTotalMW = useCallback(
    (levelId: string): number => {
      const level = levels.find((l) => l.id === levelId)
      if (!level) return 0
      const fromGroups = level.assignedGroups.reduce((sum, g) => sum + g.capacityMW, 0)
      if (fromGroups > 0) return fromGroups
      if (level.volumeCapacity?.mode === 'fixed' && level.volumeCapacity.value != null)
        return level.volumeCapacity.value
      return 0
    },
    [levels],
  )

  const getTotalMWAcrossAllLevels = useCallback((): number => {
    return levels.reduce((total, level) => {
      const levelTotal = level.assignedGroups.reduce((sum, g) => sum + g.capacityMW, 0)
      return total + levelTotal
    }, 0)
  }, [levels])

  const assignedGroupUuids = useMemo(() => {
    const uuids = new Set<string>()
    levels.forEach((level) => {
      level.assignedGroups.forEach((g) => uuids.add(g.uuid))
    })
    return uuids
  }, [levels])

  const resetLevels = useCallback(() => {
    setLevels(createInitialLevels(maxBidLevels, defaultPrices, withVolumeCapacity))
  }, [defaultPrices, maxBidLevels, withVolumeCapacity])

  const updateVolumeCapacity = useCallback((levelId: string, config: VolumeCapacityConfig) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          volumeCapacity: config,
        }
      }),
    )
  }, [])

  return useMemo(
    () => ({
      levels,
      updatePrice,
      assignGroups,
      unassignRegulationObjectGroup,
      getTotalMW,
      getTotalMWAcrossAllLevels,
      assignedGroupUuids,
      resetLevels,
      updateVolumeCapacity,
    }),
    [
      levels,
      updatePrice,
      assignGroups,
      unassignRegulationObjectGroup,
      getTotalMW,
      getTotalMWAcrossAllLevels,
      assignedGroupUuids,
      resetLevels,
      updateVolumeCapacity,
    ],
  )
}
